import { alpha } from '@mui/material/styles';
import type { CrsResultStatusName, CyberRiskScoreResult } from './types';

// Shared CRS score helpers — risk colors, labels and result-status checks.
// Reused by the CRS detail screens and the Targets table CRS cell so the
// score → color/label mapping stays in one place.

const crsResultStatusByCode: Record<number, CrsResultStatusName> = {
  1: 'Calculated',
  2: 'MissingProfile',
  3: 'NoCompletedScan',
  4: 'NoFindings',
  5: 'RateLimited',
  6: 'Unchanged',
};

export function getCrsResultStatusName(status: CyberRiskScoreResult['status']): CrsResultStatusName | null {
  if (typeof status === 'number') return crsResultStatusByCode[status] || null;
  return status;
}

// A score actually exists for these statuses (Calculated / NoFindings / Unchanged).
export function isCrsScoreReadyStatus(status: CyberRiskScoreResult['status']) {
  const statusName = getCrsResultStatusName(status);
  return statusName === 'Calculated' || statusName === 'Unchanged' || statusName === 'NoFindings';
}

// 0–10 risk scale: low scores are blue (safer), high scores trend red.
export function getRiskIndicatorColor(score: number) {
  if (score >= 9) return '#e10e0e';
  if (score >= 7) return '#ff3b30';
  if (score >= 4) return '#f9d36f';
  return '#6fd0f9';
}

export function getRiskTone(score: number) {
  const main = getRiskIndicatorColor(score);

  return {
    main,
    border: alpha(main, 0.28),
    background: alpha(main, 0.16),
  };
}

export function getRiskLabel(score: number) {
  if (score >= 8) return 'Critical risk exposure';
  if (score >= 6) return 'High risk exposure';
  if (score >= 4) return 'Moderate-high risk exposure';
  if (score >= 2) return 'Moderate risk exposure';
  return 'Low risk exposure';
}

export function getRiskPill(score: number) {
  if (score >= 8) return 'Critical';
  if (score >= 6) return 'High';
  if (score >= 4) return 'Moderate-High';
  if (score >= 2) return 'Moderate';
  return 'Low';
}
